import { IPagination, IPaginationRequest } from "interfaces/pagination";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AvatarLetter from "components/common/AvatarLetter";
import { Button } from "@mui/material";
import Header from "components/common/Header";
import { IService } from "interfaces/service";
import { IStore } from "interfaces/store";
import ServiceServices from "services/service";
import StoreServices from "services/store";
import Swal from "sweetalert2";
import Table from "components/common/Table";
import Typography from "@mui/material/Typography";
import useAlert from "hooks/useAlert";
import useToken from "hooks/useToken";
import withAuth from "hocs/withAuth";
import withPermission from "hocs/withPermission";

interface IServiceData {
  id: number;
  name: string;
  duration: number;
  sequence: number;
  last_edited_at: string;
  created_by: string;
  edited_by: string;
}

const ListService = () => {
  let line_oa_code = "CLUB_FITTING";
  const navigate = useNavigate();
  const { token } = useToken();
  const { onAlert } = useAlert();
  const [data, setData] = useState<{
    data: IServiceData[];
    pagination: IPagination;
  }>({
    data: [],
    pagination: {
      page: 1,
      total_item: 0,
      total_page: 0,
    },
  });
  const [pagination, setPagination] = useState<IPaginationRequest>({
    page: 1,
    per_page: 10,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(() => {
    ServiceServices.getAll(token as string, line_oa_code as string, {
      per_page: pagination.per_page,
      page: pagination.page,
    })
      .then((res) => {
        let paginationRes: IPagination = res.data.pagination;
        setData({
          data: res.data.data.map((d: IService) => {
            return {
              id: d.id,
              name: d.name,
              duration: d.duration,
              sequence: d.sequence,
              last_edited_at: d.last_edited_at,
              created_by: d.admin_created.name,
              edited_by: d.admin_edited.name,
            } as IServiceData;
          }),
          pagination: {
            page: paginationRes.page,
            total_item: paginationRes.total_item,
            total_page: paginationRes.total_page,
          },
        });
        setIsLoading(false);
      })
      .catch((err) => {
        onAlert("error", err.response.data.message);
        setIsLoading(false);
      });
  }, [token, onAlert, line_oa_code, pagination]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDelete = (item: IStore) => {
    Swal.fire({
      icon: "warning",
      html: `ต้องการลบบริการ <b>${item.name}</b> ใช่ หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        ServiceServices.delete(token as string, line_oa_code as string, item.id)
          .then((res) => {
            fetchData();
            onAlert("success", "ลบ Service สำเร็จ");
            setIsLoading(false);
          })
          .catch((err) => {
            onAlert("error", err.response.data.message);
            setIsLoading(false);
          });
      }
    });
  };

  return (
    <>
      <Header
        title="Service"
        breadcrumbs={[
          {
            label: "Dashboard",
            href: `/dashboard`,
          },
          {
            label: "Club Fitting",
            href: `/calendar/club-fitting`,
          },
          {
            label: "Service",
            href: "/service/club-fitting/list",
          },
          {
            label: "List",
          },
        ]}
        actions={[
          <Link to={`/service/club-fitting/create`}>
            <Button variant="contained" size="large">
              <AddIcon
                sx={{
                  fontSize: 14,
                  mr: 1,
                }}
              />
              <Typography>Create</Typography>
            </Button>
          </Link>,
        ]}
      />
      <Table
        headers={[
          {
            key: "sequence",
            label: "Sequence",
          },
          {
            key: "name",
            label: "Service Name",
          },
          {
            key: "duration",
            label: "Duration (Min)",
          },
          {
            key: "created_by",
            label: "Created By",
            render: (value) => <AvatarLetter label={value} tooltip />,
          },
          {
            key: "edited_by",
            label: "Edited By",
            render: (value) => <AvatarLetter label={value} tooltip />,
          },
          {
            key: "last_edited_at",
            label: "Last Edited At",
            render: (value) => (
              <Typography>{new Date(value).toLocaleString("th-TH")}</Typography>
            ),
          },
        ]}
        actions={{
          onDetail: (item: any) => navigate(`/service/club-fitting/${item.id}`),
          onEdit: (item: any) => navigate(`/service/club-fitting/${item.id}/edit`),
          onDelete: (item: any) => onDelete(item),
        }}
        data={data.data}
        isLoading={isLoading}
        pagination={
          data.pagination
            ? {
                ...data.pagination,
                onChange: (page) =>
                  setPagination((pagination) => {
                    return {
                      ...pagination,
                      page,
                    };
                  }),
              }
            : undefined
        }
      />
    </>
  );
};

export default withAuth(withPermission(ListService, "MANAGE_CLUB_FITTING"));
